import { useCallback, useMemo } from "react";

// Import useUser directly (not via the hooks barrel): the barrel transitively
// pulls in large component trees (useUserEvents → project context → screenshot
// components), which creates a circular import that breaks module evaluation
// in any file that loads this hook early.
import useUser from "./useUser";

/**
 * List of long-lived feature flags.
 *
 * Feature flags use INVERTED polarity from beta features: the organization
 * carries a `disabledFeatures` array, and presence of a name in that array
 * means the feature is disabled. An absent `disabledFeatures` field, or an
 * absent name within it, means the feature is ENABLED (default-true).
 */
export const FEATURE_FLAGS = [
  "Type Well Editor",
  "Project Creation",
  "Forecast Project Tab",
  "Type Well Project Tab",
  "Daily Data Toggles",
  "Data Source Toggles",
  "Single Well Multiproduct Chart",
  "Material Balance Time Chart",
  "RF vs PV Chart",
  "RF vs Time Chart",
  "Sales Chart Products",
  "XDA Widgets",
  "3DV Widgets",
  "Survey Visualizer Widget",
  "Well Completions Widget",
  "Machine Learning Widget",
  "Type Log Widget",
  "SAM Charts",
  "Map Bubble Visual",
  "Map Completion Visual",
  "Map Well Coverage Visual",
  "Map XDA Overlap Visual",
  "Active Development Timeline",
  // Prerequisite of "Midstream Allocation Model": provisioning guarantees
  // MAM-on implies Base-on, so MAM surfaces are coded against MAM alone (no
  // OR with this flag). "Base Midstream" instead gates everything MAM does
  // NOT already gate — the midstream data source toggle, facility map
  // layers, facility groupBy/entity-kind surfaces, and facility options in
  // user defaults. When off, ALL base midstream functionality disappears.
  "Base Midstream",
  "Midstream Allocation Model",
  "Midstream Export",
  // Field-group flags: one per column-service category (columnDB.categoryColor).
  // Disabling one hides that category's fields in the data table / column
  // choosers; "Sales Chart Products" additionally hides the Sales-tab products
  // in chart product selectors.
  "Header Fields",
  "Dates Fields",
  "Geology Fields",
  "Well Design Fields",
  "Frac Data Fields",
  "Parent Child Fields",
  "Pad Metrics Fields",
  "Fluid Analysis Fields",
  "Performance Fields",
  "Forecast Fields",
  "Licensed Volumes Fields",
  "Well Spacing Fields",
  "Well Count Fields",
  "SAGD Fields",
  "Enhanced Oil Recovery Fields",
  "Production Summary Fields",
  "Well Spacing Summary Fields"
  // Removed (QA 2026-08-12): "Sampled 3D Geo Fields" (no-op — those columns
  // are only delivered to orgs holding the 3DV Columns beta, so the display
  // flag gated nothing) and "Midstream Fields" (the Midstream category now
  // follows "Midstream Allocation Model" — see FIELD_GROUP_FLAG_OVERRIDES).
] as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

/**
 * Pure helper shared between `useFeatureFlags` and any non-hook contexts
 * that need to check feature flag membership without calling hooks.
 * Returns `true` (default-enabled) unless `flag` is present in
 * `disabledFeatures`.
 */
export function isFeatureEnabled(
  disabledFeatures: Maybe<string[]>,
  flag: string
): boolean {
  return !(disabledFeatures ?? []).includes(flag);
}

/**
 * Categories whose display gating rides an existing flag instead of a
 * dedicated "<category> Fields" one. The Midstream category follows the
 * allocation model (QA 2026-08-12): the MAM flag already controls delivery
 * of those columns server-side (eva-column-service), so it owns display too.
 */
const FIELD_GROUP_FLAG_OVERRIDES: Record<string, FeatureFlag> = {
  Midstream: "Midstream Allocation Model"
};

/**
 * Maps a data-table / column-service category name (e.g. "Header", "Forecast")
 * to the `FeatureFlag` gating it — usually the "<category> Fields" flag, with
 * the overrides above. Used by the data-table column chooser and other
 * field-selection surfaces (groupBy/filter field pickers) that render the same
 * server-driven categories, so disabling a field-group flag hides that
 * category's fields everywhere fields are chosen.
 */
export function getFieldGroupFlag(category: string): Maybe<FeatureFlag> {
  // hasOwn guard: category names are server-driven, so a bare lookup would
  // resolve prototype members ("constructor", "toString") as flags.
  if (Object.prototype.hasOwnProperty.call(FIELD_GROUP_FLAG_OVERRIDES, category)) {
    return FIELD_GROUP_FLAG_OVERRIDES[category];
  }
  const candidate = `${category} Fields`;
  return (FEATURE_FLAGS as readonly string[]).includes(candidate)
    ? (candidate as FeatureFlag)
    : undefined;
}

/**
 * Custom hook to check if a long-lived feature flag is enabled for the
 * current user's organization. Uses the `useUser` hook internally to get
 * the user data.
 * @returns {Object} An object with a `hasFeature` method to check if a flag is enabled.
 */
export default function useFeatureFlags() {
  const user = useUser();
  const disabledFeatures = user?.user?.organization?.disabledFeatures;

  // Memoized on disabledFeatures (not user/organization as a whole) so
  // consumers that depend on `hasFeature` in a useEffect/useMemo/useCallback
  // deps array don't recompute on every render — only when the org's
  // disabled-feature set actually changes.
  const hasFeature = useCallback(
    (flag: FeatureFlag): boolean => isFeatureEnabled(disabledFeatures, flag),
    [disabledFeatures]
  );

  return useMemo(() => ({ hasFeature }), [hasFeature]);
}
